"use client";

import { Text } from "../../ui/text";
import { Button } from "../../ui/button";
import {
  useContentImage,
  type UseContentImageProps,
} from "./use-content-image";
import ArtDirection from "../../util/art-direction/art-direction";
import { motion as m } from "motion/react";

export type ContentImageProps = UseContentImageProps;

function ContentImage(props: ContentImageProps) {
  const { sectionName, className, styles, data } = useContentImage(props);
  const { theme, preTitle, title, description, image, links } = data;

  return (
    <m.section
      className={styles.base({ className })}
      data-section-name={sectionName}
      initial="offscreen"
      whileInView="onscreen"
      viewport={{ amount: 0.8, once: true }}
    >
      <div className={styles.wrapper()}>
        <m.div
          className={styles.content()}
          variants={{
            offscreen: {
              y: 100,
              opacity: 0,
            },
            onscreen: {
              y: 0,
              opacity: 1,
              transition: {
                duration: 0.7,
                delay: 0,
                ease: "circOut",
              },
            },
          }}
        >
          <div>
            {!!preTitle && (
              <Text
                className={styles.preTitle()}
                theme={theme}
                weight="semibold"
              >
                {preTitle}
              </Text>
            )}
            <Text
              className={styles.title()}
              size="tiny"
              tag="h2"
              weight="medium"
            >
              {title}
            </Text>
            <Text
              className={styles.description()}
              size="description"
              theme={theme}
              weight="medium"
            >
              {description}
            </Text>
          </div>
          {!!links &&
            links.map((link) => (
              <Button
                appearance="solid"
                color="black"
                rounded
                key={link.text}
                href={link.url}
                title={link.text}
                endIcon={link.endIcon}
                className={styles.link()}
              >
                {link.text}
              </Button>
            ))}
        </m.div>
        <m.div
          className={styles.imageWrapper()}
          variants={{
            offscreen: {
              y: 100,
              opacity: 0,
            },
            onscreen: {
              y: 0,
              opacity: 1,
              transition: {
                duration: 0.7,
                delay: 0.3,
                ease: "circOut",
              },
            },
          }}
        >
          <ArtDirection
            desktop={image}
            className={styles.image()}
            containerClassName={styles.figure()}
          />
        </m.div>
      </div>
    </m.section>
  );
}

export default ContentImage;
